import apiClient from './apiClient';

export type ChangeRequestStatus = 'draft' | 'submitted' | 'in_review' | 'approved' | 'rejected' | 'enacted' | 'cancelled';
export type ChangeRequestType = 'scope' | 'cost' | 'schedule' | 'mixed';
export type ImpactEntityType = 'task' | 'budget_line' | 'wbs' | 'cost_code';
export type ApprovalDecision = 'pending' | 'approved' | 'rejected';

export interface ApprovalLevelPayload {
  label: string;
  approverUserId?: string | null;
  approverRole?: string | null;
}

export interface ChangeRequestImpact {
  id: string;
  changeRequestId: string;
  entityType: ImpactEntityType;
  entityId: string;
  impactDescription: string | null;
  enteredCostDelta: number | string | null;
  enteredScheduleDeltaDays: number | null;
  applied: boolean;
  appliedAt: string | null;
}

export interface ChangeRequestApproval {
  id: string;
  changeRequestId: string;
  levelIndex: number;
  label: string;
  approverUserId: string | null;
  approverRole: string | null;
  decision: ApprovalDecision;
  decidedAt: string | null;
  notes: string | null;
  approver?: { id: string; firstName?: string; lastName?: string; email?: string; role?: string } | null;
}

export interface ChangeRequest {
  id: string;
  projectId: string;
  crCode: string;
  title: string;
  description: string | null;
  changeType: ChangeRequestType;
  status: ChangeRequestStatus;
  estimatedCostImpact: number | string | null;
  estimatedScheduleImpactDays: number | null;
  computedCostImpact: number | string | null;
  computedScheduleImpactDays: number | null;
  tasksAffectedCount: number | null;
  raisedAt: string;
  submittedAt: string | null;
  decidedAt: string | null;
  enactedAt: string | null;
  raisedBy?: { id: string; firstName?: string; lastName?: string; email?: string; role?: string } | null;
  impacts?: ChangeRequestImpact[];
  approvals?: ChangeRequestApproval[];
}

export interface ChangeRequestPayload {
  projectId: string;
  title: string;
  description?: string | null;
  changeType: ChangeRequestType;
  estimatedCostImpact?: number | null;
  estimatedScheduleImpactDays?: number | null;
}

const unwrap = <T>(response: any): T => {
  const body = response?.data ?? response;
  return Object.prototype.hasOwnProperty.call(body, 'data') ? body.data as T : body as T;
};

class ChangeRequestsService {
  async list(params: { projectId?: string; status?: ChangeRequestStatus | ''; changeType?: ChangeRequestType | '' } = {}): Promise<ChangeRequest[]> {
    return unwrap<ChangeRequest[]>(await apiClient.get('/change-requests', { params }));
  }

  async get(id: string): Promise<ChangeRequest> {
    return unwrap<ChangeRequest>(await apiClient.get(`/change-requests/${id}`));
  }

  async create(payload: ChangeRequestPayload): Promise<ChangeRequest> {
    return unwrap<ChangeRequest>(await apiClient.post('/change-requests', payload));
  }

  async update(id: string, payload: Partial<ChangeRequestPayload>): Promise<ChangeRequest> {
    return unwrap<ChangeRequest>(await apiClient.patch(`/change-requests/${id}`, payload));
  }

  async addImpact(id: string, payload: {
    entityType: ImpactEntityType;
    entityId: string;
    impactDescription?: string | null;
    enteredCostDelta?: number | null;
    enteredScheduleDeltaDays?: number | null;
  }): Promise<ChangeRequestImpact> {
    return unwrap<ChangeRequestImpact>(await apiClient.post(`/change-requests/${id}/impacts`, payload));
  }

  async removeImpact(id: string, impactId: string): Promise<void> {
    await apiClient.delete(`/change-requests/${id}/impacts/${impactId}`);
  }

  async submit(id: string, chainOverride?: ApprovalLevelPayload[]): Promise<ChangeRequest> {
    return unwrap<ChangeRequest>(await apiClient.post(`/change-requests/${id}/submit`, chainOverride ? { chainOverride } : {}));
  }

  async decide(id: string, levelIndex: number, payload: { decision: 'approved' | 'rejected'; notes?: string | null }): Promise<ChangeRequestApproval> {
    return unwrap<ChangeRequestApproval>(await apiClient.post(`/change-requests/${id}/approvals/${levelIndex}`, payload));
  }

  async enact(id: string): Promise<ChangeRequest> {
    return unwrap<ChangeRequest>(await apiClient.post(`/change-requests/${id}/enact`));
  }

  async cancel(id: string, reason: string): Promise<ChangeRequest> {
    return unwrap<ChangeRequest>(await apiClient.post(`/change-requests/${id}/cancel`, { reason }));
  }

  async getApprovalChain(projectId: string): Promise<ApprovalLevelPayload[]> {
    return unwrap<ApprovalLevelPayload[]>(await apiClient.get(`/projects/${projectId}/cr-approval-chain`));
  }

  async setApprovalChain(projectId: string, levels: ApprovalLevelPayload[]): Promise<ApprovalLevelPayload[]> {
    return unwrap<ApprovalLevelPayload[]>(await apiClient.put(`/projects/${projectId}/cr-approval-chain`, { levels }));
  }

  async pendingApprovals(): Promise<ChangeRequest[]> {
    return unwrap<ChangeRequest[]>(await apiClient.get('/change-requests/pending-approvals'));
  }
}

export default new ChangeRequestsService();
